const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/LoadingSpinner/LoadingSpinner.tsx';
import _ from 'lodash';
import CircularProgress, { CircularProgressProps } from '@mui/material/CircularProgress';
import { useEffect, useState } from 'react';
import { css, cx } from '@emotion/css';
import { Stack, Typography } from '@mui/material';

interface Props extends CircularProgressProps {
    className?: string;
    /**
     * If set, the spinner will be centered.
     * - absolute: within the parent
     * - fixed: within the view port
     * - alignSelf: within Flexbox container
     */
    center?: 'absolute' | 'fixed' | 'alignSelf';

    /**
     * Factor to change the size and thickness
     * - default: 1
     * - Try to keep it consistent. So far only the custom values 0.5 and 0.75 are used.
     */
    sizeFactor?: number;

    /**
     * Milliseconds to wait until the spinner is shown
     */
    delay?: number;
    overlay?: boolean;
    showLabel?: boolean;
}

const loadingSpinnerOverlayClassName = css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.7);
    z-index: 99999;
`;

const baseStyle = css`
    z-index: 1000;
    height: 40px;
    width: 40px;
`;

export default function LoadingSpinner(props: Props) {
    const { showLabel = false } = props;
    const hasDelay = (props.delay || 0) > 0;
    const [isShown, setIsShown] = useState(!hasDelay); // initially shown when no delay

    useEffect(() => {
        if (!hasDelay) {
            return undefined;
        } else {
            const timerId = window.setTimeout(() => setIsShown(true), props.delay);
            return () => window.clearTimeout(timerId);
        }
    }, []);

    if (!isShown) {
        return null;
    }

    const spinnerProps: CircularProgressProps = {
        variant: 'indeterminate',
        size: (props.sizeFactor || 1) * 40,
        thickness: Math.sqrt(props.sizeFactor || 1) * 3.6, // without sqrt thickness would grow quadratically
        ..._.omit(props, ['center', 'sizeFactor', 'delay', 'overlay', 'showLabel']),
    };

    let positionStyle;

    if (props.center) {
        switch (props.center) {
            case 'absolute':
            case 'fixed':
                positionStyle = css`
                    ${baseStyle};
                    position: ${props.center};
                    left: 50%;
                    top: 50%;
                    transform: translateX(-${(spinnerProps.size as number) / 2}px)
                        translateY(-${(spinnerProps.size as number) / 2}px);
                `;
                break;
            case 'alignSelf':
                positionStyle = css`
                    ${baseStyle};
                    margin: auto;
                `;
                break;
        }
    }

    const spinner = (
        <div className={cx('LoadingSpinner', positionStyle, props.className)}>
            <Stack
                direction={'column'}
                spacing={2}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress {...spinnerProps} />
                {showLabel ? <Typography variant={'caption'}>Loading...</Typography> : null}
            </Stack>
        </div>
    );

    return props.overlay ? <div className={loadingSpinnerOverlayClassName}>{spinner}</div> : spinner;
}
